import {
  Action,
  NavigationRoute,
  Server,
  findNavigationRouteInActions,
  findSubmittalAction,
} from '@nextforce/api'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { QueryClient } from '@tanstack/react-query'
import { navigationRef } from 'app/linking'
import { getDeviceToken } from 'app/server/device_token'
import { getClientEnvironment, getClientPlatform } from 'app/server/platform'
import { refetchMessagesIfNotExist } from 'chat/messages/queries/list'
import * as Notifications from 'expo-notifications'
import { AppState, Platform } from 'react-native'
import { buildStorageKey } from 'shared/storage'

export interface PushNotificationInfo {
  route: NavigationRoute
  event: string | undefined
  markAsReadAction: Action | undefined
}

export async function registerDevice(
  server: Server,
  client: QueryClient
): Promise<string> {
  const status = await requestPermission()
  if (Platform.OS !== 'web' && status !== 'granted') {
    throw new Error('Permission not granted to receive push notifications')
  }

  showForegroundNotifications(client)
  Notifications.addNotificationReceivedListener(async notification => {
    if (
      !isDataOnlyPushNotification(notification) ||
      AppState.currentState !== 'active'
    ) {
      return
    }

    const behavior = await getForegroundNotificationBehavior(
      notification,
      client
    )

    if (!shouldPresentNotification(behavior)) {
      return
    }

    await scheduleForegroundNotification(notification)
  })
  const token = await getDeviceToken()
  await setNotificationChannel()
  await server.post('/v2/users/me/devices', {
    token,
    platform: getClientPlatform(),
    env: getClientEnvironment(),
  })
  return token
}

export async function deregisterDevice(server: Server, token: string) {
  const platform = encodeURIComponent(getClientPlatform())
  const env = encodeURIComponent(getClientEnvironment())
  const encodedToken = encodeURIComponent(token)

  return server.delete(
    `/v2/users/me/devices?token=${encodedToken}&platform=${platform}&env=${env}`
  )
}

export function getPushNotificationInfo(
  response: Notifications.NotificationResponse
): PushNotificationInfo | undefined {
  const data = getDataFromNotificationResponse(response)

  if (!data) {
    return
  }

  const route = findNavigationRouteInActions(data.actions)

  if (!route) {
    return
  }

  return {
    route,
    event: data.event,
    markAsReadAction: findSubmittalAction(data.actions),
  }
}

function getDataFromNotificationResponse(
  response: Notifications.NotificationResponse
):
  | { actions: Action[]; event: string | undefined; badge: number | undefined }
  | undefined {
  const wasTapped =
    response.actionIdentifier === Notifications.DEFAULT_ACTION_IDENTIFIER

  if (!wasTapped) {
    return
  }

  const contentData = getDataFromNotificationData(
    response.notification.request.content.data
  )

  if (contentData) {
    return contentData
  }

  const trigger = response.notification.request.trigger

  if (trigger && 'type' in trigger && trigger.type !== 'push') {
    return
  }

  if (trigger && 'payload' in trigger) {
    return getDataFromPayload(trigger.payload)
  } else if (trigger && 'remoteMessage' in trigger) {
    return getDataFromNotificationData(trigger.remoteMessage?.data)
  } else {
    return
  }
}

export const SIMULATOR_ERROR = 'SIMULATOR_ERROR'

export async function saveWebDeviceToken(deviceToken: string) {
  return await AsyncStorage.setItem(
    buildStorageKey('device_token'),
    deviceToken
  )
}

function showForegroundNotifications(client: QueryClient) {
  Notifications.setNotificationHandler({
    handleNotification: async notification => {
      const behavior = await getForegroundNotificationBehavior(
        notification,
        client
      )
      return behavior
    },
  })
}

function getDataFromPayload(
  payload: Record<string, unknown> | null | undefined
):
  | { actions: Action[]; event: string | undefined; badge: number | undefined }
  | undefined {
  if (!payload) {
    return
  }

  const parsedActions = parseActions(payload.actions_v3)

  if (!parsedActions) {
    return
  }

  return {
    actions: parsedActions,
    event: asOptionalString(payload.event),
    badge: getBadgeValue(
      (payload.aps as { badge?: unknown } | undefined)?.badge
    ),
  }
}

function getDataFromNotificationData(
  data: Record<string, unknown> | null | undefined
):
  | { actions: Action[]; event: string | undefined; badge: number | undefined }
  | undefined {
  if (!data) {
    return
  }

  const parsedActions = parseActions(data.actions_v3)

  if (!parsedActions) {
    return
  }

  return {
    actions: parsedActions,
    event: asOptionalString(data.event),
    badge: getBadgeValue(data.badge),
  }
}

function parseActions(actions: unknown): Action[] | undefined {
  if (Array.isArray(actions)) {
    return actions as Action[]
  }

  if (typeof actions !== 'string') {
    return
  }

  try {
    const parsedActions = JSON.parse(actions)
    return Array.isArray(parsedActions)
      ? (parsedActions as Action[])
      : undefined
  } catch (error) {
    return
  }
}

function asOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function getBadgeValue(value: unknown): number | undefined {
  const badge = Number(value)

  return Number.isFinite(badge) ? badge : undefined
}

async function getForegroundNotificationBehavior(
  notification: Notifications.Notification,
  client: QueryClient
) {
  let shouldShow = true
  const currentRoute = navigationRef.getCurrentRoute()

  const data = getDataFromNotificationResponse({
    notification,
    actionIdentifier: Notifications.DEFAULT_ACTION_IDENTIFIER,
  })

  if (data?.badge !== undefined) {
    await Notifications.setBadgeCountAsync(data.badge)
  }

  const navigationRouteFromNotification = findNavigationRouteInActions(
    data?.actions
  )

  if (
    navigationRouteFromNotification?.name === 'messages' &&
    navigationRouteFromNotification.params
  ) {
    refetchMessagesIfNotExist(
      client,
      navigationRouteFromNotification.params?.messageId
    ).catch(e => console.log(e))

    if (
      'id' in navigationRouteFromNotification.params &&
      currentRoute?.params &&
      'id' in currentRoute.params
    ) {
      const currentConversationId = currentRoute.params['id'] as string
      const conversationIdFromNotification = navigationRouteFromNotification
        .params.id as string
      shouldShow = currentConversationId !== conversationIdFromNotification
    }
  }

  return {
    shouldShowAlert: shouldShow,
    shouldShowBanner: shouldShow,
    shouldShowList: shouldShow,
    shouldPlaySound: true,
    shouldSetBadge: false,
  }
}

function shouldPresentNotification(
  behavior: Awaited<ReturnType<typeof getForegroundNotificationBehavior>>
) {
  return (
    behavior.shouldShowAlert ||
    behavior.shouldShowBanner ||
    behavior.shouldShowList
  )
}

function isDataOnlyPushNotification(notification: Notifications.Notification) {
  const trigger = notification.request.trigger

  return Boolean(
    trigger &&
      'type' in trigger &&
      trigger.type === 'push' &&
      'remoteMessage' in trigger &&
      trigger.remoteMessage?.data &&
      !trigger.remoteMessage.notification
  )
}

async function scheduleForegroundNotification(
  notification: Notifications.Notification
) {
  const trigger = notification.request.trigger

  if (!(trigger && 'remoteMessage' in trigger && trigger.remoteMessage?.data)) {
    return
  }

  const data = trigger.remoteMessage.data as Record<string, unknown>
  const title = asOptionalString(data.title)
  const body = asOptionalString(data.message)

  if (!title && !body) {
    return
  }

  await Notifications.scheduleNotificationAsync({
    content: {
      title,
      body,
      data,
      sound: true,
      ...(Platform.OS === 'android' ? { channelId: 'default' } : {}),
    },
    trigger: null,
  })
}

async function setNotificationChannel() {
  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'default',
      importance: Notifications.AndroidImportance.MAX,
    })
  }
}

async function requestPermission() {
  if (Platform.OS === 'web') {
    if (!('Notification' in window)) {
      console.log('Browser does not support desktop notifications')
      return 'denied'
    } else if (Notification.permission === 'granted') {
      return 'granted'
    } else if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission()
      return permission
    } else {
      return 'denied'
    }
  } else {
    const currentPermissions = await Notifications.getPermissionsAsync()

    if (currentPermissions.status === 'granted') {
      return 'granted'
    }

    const requestedPermissions = await Notifications.requestPermissionsAsync()
    return requestedPermissions.status
  }
}
